import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronsUpDown, Search, X } from 'lucide-react';

export interface MultiSelectPopoverOption {
  id: string;
  label: string;
  secondaryLabel?: string;
  disabled?: boolean;
}

interface MultiSelectPopoverProps {
  value: string[];
  options: MultiSelectPopoverOption[];
  onChange: (nextValue: string[]) => void;
  placeholder: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  noMatchesMessage?: string;
  disabled?: boolean;
  showSelectAll?: boolean;
  selectAllLabel?: string;
}

export function MultiSelectPopover({
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No options found',
  noMatchesMessage = 'No matching options',
  disabled = false,
  showSelectAll = false,
  selectAllLabel = 'Select all',
}: MultiSelectPopoverProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedIds = useMemo(() => new Set(value), [value]);

  const selectedOptions = useMemo(
    () => options.filter((option) => selectedIds.has(option.id)),
    [options, selectedIds],
  );

  const filteredOptions = useMemo(() => {
    if (!search) return options;

    const normalizedSearch = search.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearch)
      || option.secondaryLabel?.toLowerCase().includes(normalizedSearch),
    );
  }, [options, search]);

  const filteredSelectableIds = useMemo(
    () => filteredOptions.filter((option) => !option.disabled).map((option) => option.id),
    [filteredOptions],
  );

  const allFilteredSelected =
    filteredSelectableIds.length > 0 && filteredSelectableIds.every((id) => selectedIds.has(id));
  const handleToggle = (id: string) => {
    const nextIds = new Set(value);
    if (nextIds.has(id)) {
      nextIds.delete(id);
    } else {
      nextIds.add(id);
    }
    onChange(Array.from(nextIds));
  };

  const handleToggleAll = (checked: boolean) => {
    const nextIds = new Set(value);

    for (const id of filteredSelectableIds) {
      if (checked) {
        nextIds.add(id);
      } else {
        nextIds.delete(id);
      }
    }

    onChange(Array.from(nextIds));
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal h-auto min-h-9"
          disabled={disabled}
        >
          <div className="flex flex-wrap gap-1 py-0.5">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <Badge key={option.id} variant="secondary" className="text-xs font-normal">
                  {option.label}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="p-2 border-b">
          <div className="flex items-center gap-2 px-1">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="max-h-56 overflow-y-auto p-1">
          {showSelectAll && filteredSelectableIds.length > 0 && (
            <label className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer border-b">
              <Checkbox
                checked={allFilteredSelected}
                onCheckedChange={(checked) => handleToggleAll(checked === true)}
                disabled={disabled}
              />
              <span>{selectAllLabel}</span>
            </label>
          )}
          {filteredOptions.length === 0 ? (
            <div className="px-2 py-3 text-xs text-muted-foreground text-center">
              {options.length === 0 ? emptyMessage : noMatchesMessage}
            </div>
          ) : (
            filteredOptions.map((option) => (
              <label
                key={option.id}
                className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer"
              >
                <Checkbox
                  checked={selectedIds.has(option.id)}
                  onCheckedChange={() => handleToggle(option.id)}
                  disabled={disabled || option.disabled}
                />
                <span>{option.label}</span>
                {option.secondaryLabel && (
                  <span className="text-[11px] text-muted-foreground ml-auto truncate max-w-[140px]">
                    {option.secondaryLabel}
                  </span>
                )}
              </label>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
